import classNames from "classnames/bind";
import { produce } from "immer";
import React from "react";
import { DropdownInputBare } from "../../DataInput/DropdownInput";
import {
  emptyFieldKey,
  generateSampleValueForFilterBuilder,
  getDeepReference,
} from "../FilterBuilder.helpers";
import styles from "../FilterBuilder.module.scss";
import {
  FilterBuilderAllowedOperatorsMapping,
  FilterBuilderFieldDefinition,
  FilterBuilderFieldsType,
  FilterBuilderFilterType,
  FilterBuilderProps,
} from "../types";

const cx = classNames.bind(styles);

export const FilterBuilderFieldPicker = ({
  setFilter,
  onFilterChange,
  fieldKey = "",
  myKey,
  availableFields,
  onChange,
  fields,
  isDisabled,
  defaultSingleField,
  operatorMappingOverride,
}: {
  myKey: Array<string | number>;
  fieldKey?: string;
  setFilter: React.Dispatch<React.SetStateAction<FilterBuilderFilterType>>;
  onFilterChange?: FilterBuilderProps["onFilterChange"];
  availableFields?: string[];
  onChange?: () => void;
  fields: FilterBuilderFieldsType;
  isDisabled?: boolean;
  defaultSingleField?: string;
  operatorMappingOverride?: FilterBuilderAllowedOperatorsMapping;
}) => {
  if (defaultSingleField) return null;

  const fieldEntries: Array<[string, FilterBuilderFieldDefinition]> =
    availableFields
      ? availableFields.map((field) => [field, fields[field]])
      : Object.entries(fields);

  const fieldsOptions = fieldEntries.map(([value, fieldDefinition]) => ({
    value,
    label: fieldDefinition.display_name,
  }));

  return (
    <DropdownInputBare
      isFluid
      isInline
      className={cx("dropdown")}
      name=""
      shouldWrap
      noSearchDebounce
      onChange={(newKeyName: string) => {
        const newFieldDefinition = fields[newKeyName];
        const sampleValue = generateSampleValueForFilterBuilder({
          data_type: newFieldDefinition.data_type,
          formatTo:
            newFieldDefinition.data_type === "date"
              ? newFieldDefinition.format
              : undefined,
          operatorMappingOverride,
        });

        if (sampleValue === undefined) return;

        // When adding a new field:
        if (myKey.at(myKey.length - 1) === emptyFieldKey) {
          const newKeys = myKey.slice(0, -2);

          setFilter((oldFilter: FilterBuilderFilterType) => {
            const producedFilter = produce(
              oldFilter,
              (draft: FilterBuilderFilterType) => {
                const targetToAddNewField = getDeepReference(draft, newKeys);
                if (Array.isArray(getDeepReference(oldFilter, newKeys))) {
                  (targetToAddNewField as any[]).push({
                    [newKeyName]: sampleValue,
                  });
                } else {
                  (targetToAddNewField ?? draft)[newKeyName] = sampleValue;
                }
              }
            );

            onFilterChange?.(producedFilter);
            return producedFilter;
          });
        } else {
          // When editing an existing field:
          const oldKey = fieldKey;
          const newKey = newKeyName;

          setFilter((oldFilter: FilterBuilderFilterType) => {
            const producedFilter = produce(
              oldFilter,
              (draft: FilterBuilderFilterType) => {
                const ref =
                  getDeepReference(draft, myKey.slice(0, -1)) ?? draft;
                delete Object.assign(ref, {
                  [newKey]: sampleValue,
                })[oldKey];
              }
            );

            onFilterChange?.(producedFilter);
            return producedFilter;
          });
        }

        onChange?.();
      }}
      value={fieldKey}
      options={fieldsOptions}
      valueKey="value"
      labelKey="label"
      isClearable={false}
      placeholder="Select Attribute..."
      isDisabled={isDisabled}
    />
  );
};
